import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { kiVorlagenVerwaltungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * **KI-Vorlagen verwalten** (Nils, 18.08.2026: *„da kann ich dann Prompts eingeben, was der
 * aus so einer Aufgabe machen soll – wie formulieren usw."*).
 *
 * Gepflegt wird hier, benutzt im Anlegen-Dialog (`AufgabeAnlegen`). Die Trennung ist
 * Absicht: eine Anweisung, die beim Anlegen eingetippt wird, ist jedes Mal eine andere – und
 * dann sind auch die Ergebnisse nicht vergleichbar.
 *
 * ## Warum der Prompt hier in voller Länge steht
 *
 * Weil er das eigentliche Werkzeug ist. Eine Verwaltung, die nur Namen listet und zum
 * Bearbeiten einen zweiten Dialog öffnet, macht aus dem Nachschärfen – dem Normalfall – drei
 * Klicks. Hier ist die Zeile das Formular.
 *
 * ## Warum es keinen Probelauf gibt wie bei den Triage-Regeln
 *
 * Eine Regel greift **still** in den Bestand; sie muss man sehen, bevor man sie scharf
 * stellt. Eine Vorlage schlägt einem Menschen etwas vor, der davorsitzt und ablehnen kann –
 * ihr Probelauf ist der nächste Knopfdruck im Anlegen-Dialog.
 */
export function KIVorlagen() {
  const queryClient = useQueryClient()
  const { data: vorlagen = [], error } = useQuery(kiVorlagenVerwaltungQuery)
  const [offen, setOffen] = useState<string | 'neu' | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['ki'] })
  }

  const loeschen = useMutation({
    mutationFn: (id: string) => trpc.ki.vorlageLoeschen.mutate({ id }),
    onSuccess: neuLaden,
    onError: (e: Error) => setMeldung(e.message),
  })

  /** Kein Admin – die Abfrage schlägt fehl, und die Karte blendet sich aus. */
  if (error) return null

  return (
    <Card className="mt-3">
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-[13.5px] font-bold">KI-Vorlagen</h3>
          <span className="text-[11.5px] text-muted">
            Anweisungen, die im Anlegen-Dialog auf einen Entwurf angewendet werden können
          </span>
        </div>

        {vorlagen.length === 0 && offen !== 'neu' && (
          <p className="mt-2 text-[12.5px] text-muted">
            Keine Vorlage hinterlegt – der KI-Knopf im Anlegen-Dialog bleibt so lange ausgeblendet.
          </p>
        )}

        <div className="mt-2 flex flex-col gap-1.5">
          {vorlagen.map((v) =>
            offen === v.id ? (
              <VorlagenFormular
                key={v.id}
                vorlage={v}
                fertig={() => {
                  setOffen(null)
                  neuLaden()
                }}
                abbrechen={() => setOffen(null)}
              />
            ) : (
              <div
                key={v.id}
                className={`rounded-lg border border-line px-2.5 py-2 ${v.aktiv ? '' : 'opacity-55'}`}
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <strong className="text-[12.5px]">{v.name}</strong>
                  {!v.aktiv && <span className="text-[11px] text-muted">abgeschaltet</span>}
                  {v.ordnetEin && (
                    <span
                      className="rounded-md bg-accent-soft px-1.5 py-px text-[10.5px] text-accent-deep"
                      title="Darf auch Pool, Priorität, Größe und Zeitfenster vorschlagen"
                    >
                      ordnet ein
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-muted">
                    {/**
                     * **Die Steuergröße** (Kap. 7, eine Ebene unter der Quote je Modell): eine
                     * Vorlage, deren Vorschläge nie übernommen werden, ist eine Rechnung.
                     */}
                    {v.benutzt === 0
                      ? 'noch nie benutzt'
                      : `${v.uebernommen} von ${v.benutzt} übernommen`}
                  </span>
                </div>
                {v.hinweis && <p className="mt-0.5 text-[11.5px] text-ink2">{v.hinweis}</p>}
                <p className="mt-1 line-clamp-2 text-[11.5px] whitespace-pre-wrap text-muted">
                  {v.prompt}
                </p>
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOffen(v.id)}
                    className="cursor-pointer text-[11.5px] font-semibold text-accent-deep hover:underline"
                  >
                    bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`„${v.name}" löschen?`)) loeschen.mutate(v.id)
                    }}
                    className="cursor-pointer text-[11.5px] text-muted hover:underline"
                  >
                    löschen
                  </button>
                </div>
              </div>
            ),
          )}
        </div>

        {offen === 'neu' ? (
          <VorlagenFormular
            fertig={() => {
              setOffen(null)
              neuLaden()
            }}
            abbrechen={() => setOffen(null)}
          />
        ) : (
          <div className="mt-2">
            <Button variant="ghost" onClick={() => setOffen('neu')}>
              + Neue Vorlage
            </Button>
          </div>
        )}

        {meldung && <p className="mt-2 text-[12px] text-[#b02a2a]">{meldung}</p>}
      </CardPad>
    </Card>
  )
}

interface VorlagenDaten {
  id: string
  name: string
  hinweis: string | null
  prompt: string
  ordnetEin: boolean
  aktiv: boolean
  reihenfolge: number
}

/**
 * Anlegen und Bearbeiten in **einem** Formular.
 *
 * Zwei Formulare für denselben Datensatz laufen auseinander – eines bekommt ein Feld, das
 * andere nicht, und es fällt erst auf, wenn jemand etwas nicht ändern kann, das er anlegen
 * durfte.
 */
function VorlagenFormular({
  vorlage,
  fertig,
  abbrechen,
}: {
  vorlage?: VorlagenDaten
  fertig: () => void
  abbrechen: () => void
}) {
  const [name, setName] = useState(vorlage?.name ?? '')
  const [hinweis, setHinweis] = useState(vorlage?.hinweis ?? '')
  const [prompt, setPrompt] = useState(vorlage?.prompt ?? '')
  const [ordnetEin, setOrdnetEin] = useState(vorlage?.ordnetEin ?? true)
  const [aktiv, setAktiv] = useState(vorlage?.aktiv ?? true)
  const [reihenfolge, setReihenfolge] = useState(String(vorlage?.reihenfolge ?? 100))
  const [fehler, setFehler] = useState<string | null>(null)

  const speichern = useMutation({
    mutationFn: () =>
      trpc.ki.vorlageSpeichern.mutate({
        ...(vorlage ? { id: vorlage.id } : {}),
        name: name.trim(),
        hinweis: hinweis.trim() || null,
        prompt: prompt.trim(),
        ordnetEin,
        aktiv,
        reihenfolge: Number(reihenfolge) || 100,
      }),
    onSuccess: fertig,
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <div className="mt-2 rounded-lg border border-accent bg-accent-soft/30 p-2.5">
      <div className="grid grid-cols-[1fr_1fr_80px] gap-2">
        <label className="block text-[11.5px] font-semibold text-ink2">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sauber formulieren"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-1.5 text-[12.5px] font-normal"
          />
        </label>
        <label className="block text-[11.5px] font-semibold text-ink2">
          Hinweis <span className="font-normal text-muted">im Menü darunter</span>
          <input
            value={hinweis}
            onChange={(e) => setHinweis(e.target.value)}
            placeholder="Macht aus Stichworten eine verständliche Aufgabe"
            className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-1.5 text-[12.5px] font-normal"
          />
        </label>
        <label className="block text-[11.5px] font-semibold text-ink2">
          Reihenfolge
          <input
            value={reihenfolge}
            onChange={(e) => setReihenfolge(e.target.value)}
            inputMode="numeric"
            className="tabular mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-1.5 text-[12.5px] font-normal"
          />
        </label>
      </div>

      <label className="mt-2 block text-[11.5px] font-semibold text-ink2">
        Anweisung an die KI
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          placeholder={
            'Formuliere Titel und Beschreibung so, dass eine Entwicklerin ohne Rückfragen ' +
            'anfangen kann: erst der Sachverhalt, dann das gewünschte Ergebnis, dann bekannte ' +
            'Randbedingungen. Keine Höflichkeitsfloskeln, keine Anrede, keine Vermutungen.'
          }
          className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[12.5px] font-normal"
        />
      </label>

      <div className="mt-1.5 flex flex-wrap items-center gap-4 text-[11.5px]">
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={ordnetEin}
            onChange={(e) => setOrdnetEin(e.target.checked)}
            className="cursor-pointer"
          />
          darf auch einordnen{' '}
          <span className="text-muted">(Pool, Priorität, Größe, Zeitfenster)</span>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={aktiv}
            onChange={(e) => setAktiv(e.target.checked)}
            className="cursor-pointer"
          />
          aktiv
        </label>
      </div>

      {fehler && <p className="mt-1.5 text-[12px] text-[#b02a2a]">{fehler}</p>}

      <div className="mt-2 flex gap-2">
        <Button
          variant="primary"
          onClick={() => {
            setFehler(null)
            speichern.mutate()
          }}
          disabled={speichern.isPending}
        >
          {speichern.isPending ? 'Speichert …' : 'Speichern'}
        </Button>
        <Button variant="ghost" onClick={abbrechen}>
          Abbrechen
        </Button>
      </div>
    </div>
  )
}
